/**
 * Toast Deduplication System
 *
 * Provides cross-tab deduplication for toast notifications using:
 * - Primary key: notificationId / id
 * - Secondary key: dedupeKey (for custom dedupe scenarios)
 * - Tertiary key: (level, message) for generic error dedupe
 *
 * Cross-tab sync: BroadcastChannel API with localStorage event fallback
 */

type ToastId = string | number;
type Timestamp = number;
type ToastLevel = 'success' | 'error' | 'warning' | 'info';

/** Shared default auto-dismiss timeout for standard toasts (10s). */
export const DEFAULT_TOAST_DURATION_MS = 10_000;

/** Configuration for dedupe behavior */
export type TToastDedupeConfig = {
  /** TTL in ms for dedupe entries (default: 30s for rematch window) */
  ttlMs: number;
  /** BroadcastChannel name for cross-tab sync */
  channelName: string;
  /** localStorage key for fallback sync */
  storageKey: string;
  /** Interval for cleanup of expired entries */
  cleanupIntervalMs: number;
};

/** Options when showing a toast */
export type TToastDedupeOptions = {
  /** Primary dedupe key (notification ID from server) */
  id?: ToastId;
  /** Secondary dedupe key (custom, e.g., dedupeKey from notification) */
  dedupeKey?: string;
  /** Toast level for generic error dedupe */
  level?: ToastLevel;
  /** Message for generic dedupe */
  message?: string;
};

/** Internal entry in the dedupe cache */
type TDedupeEntry = {
  timestamp: Timestamp;
  /** The key that was used to identify this toast */
  key: string;
};

/** Message broadcast across tabs */
type TBroadcastMessage = {
  type: 'toast-shown';
  key: string;
  timestamp: Timestamp;
};

const DEFAULT_CONFIG: TToastDedupeConfig = {
  ttlMs: 30_000, // 30 seconds - covers rematch toast window
  channelName: 'skillarea-toast-sync',
  storageKey: 'skillarea-toast-dedupe',
  cleanupIntervalMs: 60_000, // 1 minute cleanup interval
};

/**
 * Generates a composite key for dedupe lookup
 */
function generateDedupeKey(options: TToastDedupeOptions): string | null {
  // Priority 1: explicit id (notificationId from server)
  if (options.id) {
    return `id:${options.id}`;
  }

  // Priority 2: explicit dedupeKey
  if (options.dedupeKey) {
    return `dedupe:${options.dedupeKey}`;
  }

  // Priority 3: level + message combo for generic error dedupe
  if (options.level && options.message) {
    return `level:${options.level}:${options.message}`;
  }

  // No dedupe key available - allow toast
  return null;
}

/**
 * Toast Deduplicator with cross-tab support
 *
 * Usage:
 * ```typescript
 * const deduper = getToastDeduper();
 *
 * if (deduper.shouldShow({ id: 'notification-123' })) {
 *   toast.success('Message', { id: 'notification-123' });
 * }
 * ```
 */
class ToastDeduplicator {
  private config: TToastDedupeConfig;
  private cache: Map<string, TDedupeEntry> = new Map();
  private channel: BroadcastChannel | null = null;
  private cleanupTimer: ReturnType<typeof setInterval> | null = null;
  private initialized = false;

  constructor(config: Partial<TToastDedupeConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.init();
  }

  /**
   * Initialize cross-tab listeners (lazy, SSR-safe)
   */
  private init(): void {
    if (this.initialized || typeof window === 'undefined') {
      return;
    }
    this.initialized = true;

    // Setup BroadcastChannel (preferred)
    if (typeof BroadcastChannel !== 'undefined') {
      try {
        this.channel = new BroadcastChannel(this.config.channelName);
        this.channel.onmessage = (event: MessageEvent<TBroadcastMessage>) => {
          this.handleBroadcast(event.data);
        };
      } catch {
        // BroadcastChannel not available, fallback to storage events
        this.channel = null;
      }
    }

    // Setup localStorage event fallback
    window.addEventListener('storage', this.handleStorageEvent);

    // Start periodic cleanup
    this.cleanupTimer = setInterval(
      () => this.cleanup(),
      this.config.cleanupIntervalMs,
    );
  }

  /**
   * Handle broadcast from other tabs
   */
  private handleBroadcast = (data: TBroadcastMessage): void => {
    if (data.type === 'toast-shown') {
      this.cache.set(data.key, {
        timestamp: data.timestamp,
        key: data.key,
      });
    }
  };

  /**
   * Handle localStorage storage events (fallback for cross-tab)
   */
  private handleStorageEvent = (event: StorageEvent): void => {
    if (event.key !== this.config.storageKey || !event.newValue) {
      return;
    }

    try {
      const data = JSON.parse(event.newValue) as TBroadcastMessage;
      if (data.type === 'toast-shown') {
        this.cache.set(data.key, {
          timestamp: data.timestamp,
          key: data.key,
        });
      }
    } catch {
      // Invalid JSON, ignore
    }
  };

  /**
   * Broadcast toast-shown event to other tabs
   */
  private broadcast(key: string, timestamp: Timestamp): void {
    const message: TBroadcastMessage = {
      type: 'toast-shown',
      key,
      timestamp,
    };

    // BroadcastChannel (preferred)
    if (this.channel) {
      try {
        this.channel.postMessage(message);
      } catch {
        // Channel closed or error, ignore
      }
    }

    // localStorage fallback (triggers storage event in other tabs)
    try {
      localStorage.setItem(this.config.storageKey, JSON.stringify(message));
      // Remove immediately - we just needed the event to fire
      localStorage.removeItem(this.config.storageKey);
    } catch {
      // localStorage not available or quota exceeded, ignore
    }
  }

  /**
   * Clean up expired entries from cache
   */
  private cleanup(): void {
    const now = Date.now();
    const expiredKeys: string[] = [];

    this.cache.forEach((entry, key) => {
      if (now - entry.timestamp > this.config.ttlMs) {
        expiredKeys.push(key);
      }
    });

    for (const key of expiredKeys) {
      this.cache.delete(key);
    }
  }

  /**
   * Check if a toast should be shown (not duplicate within TTL)
   *
   * @param options - Dedupe options (id, dedupeKey, level+message)
   * @returns true if toast should be shown, false if it's a duplicate
   */
  shouldShow(options: TToastDedupeOptions): boolean {
    const key = generateDedupeKey(options);

    // No dedupe key - allow toast (backward compatible)
    if (!key) {
      return true;
    }

    const now = Date.now();
    const existing = this.cache.get(key);

    // Check if within TTL window
    if (existing && now - existing.timestamp < this.config.ttlMs) {
      return false; // Duplicate within TTL
    }

    // Mark as shown
    this.cache.set(key, { timestamp: now, key });
    this.broadcast(key, now);

    return true;
  }

  /**
   * Manually mark a toast as shown (for external use)
   */
  markShown(options: TToastDedupeOptions): void {
    const key = generateDedupeKey(options);
    if (!key) return;

    const now = Date.now();
    this.cache.set(key, { timestamp: now, key });
    this.broadcast(key, now);
  }

  /**
   * Clear a specific dedupe entry (e.g., when toast is dismissed)
   */
  clear(options: TToastDedupeOptions): void {
    const key = generateDedupeKey(options);
    if (key) {
      this.cache.delete(key);
    }
  }

  /**
   * Clear all dedupe entries
   */
  clearAll(): void {
    this.cache.clear();
  }

  /**
   * Cleanup resources (for tests or unmounting)
   */
  destroy(): void {
    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = null;
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('storage', this.handleStorageEvent);
    }
    this.cache.clear();
    this.initialized = false;
  }

  /**
   * Get current config (for testing/debugging)
   */
  getConfig(): TToastDedupeConfig {
    return { ...this.config };
  }

  /**
   * Get cache size (for testing/debugging)
   */
  getCacheSize(): number {
    return this.cache.size;
  }
}

// Singleton instance for app-wide dedupe
let deduperInstance: ToastDeduplicator | null = null;

/**
 * Get the singleton toast deduplicator instance
 */
export function getToastDeduper(
  config?: Partial<TToastDedupeConfig>,
): ToastDeduplicator {
  if (!deduperInstance) {
    deduperInstance = new ToastDeduplicator(config);
  }
  return deduperInstance;
}

/**
 * Reset the singleton (for testing)
 */
export function resetToastDeduper(): void {
  if (deduperInstance) {
    deduperInstance.destroy();
    deduperInstance = null;
  }
}

// Re-export class for direct instantiation (testing)
export { ToastDeduplicator };
